/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, Unlock, KeyRound, Mail, FileText, Eye, Loader2, RefreshCw, CheckCircle2, AlertTriangle, Fingerprint, LockKeyhole, EyeOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { downloadBuffer } from '@/lib/cryptoLocal';
import { portalSession, portalOtpRequest, portalOtpVerify, portalLock, createPatientKeys, unlockPatientKeys, hasUnlockedKey, listMyReports, decryptReport, getPortalToken } from '@/lib/portal';
import { E2EBadge, ReportsList, ReportViewerDialog } from '@/components/patient/portal/PortalReports';

/**
 * PatientPortalPage — /portale
 * Flusso: OTP email (2° fattore) → passphrase (sblocco chiave privata) → referti decifrati NEL BROWSER.
 */


export default function PatientPortalPage() {
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // OTP
  const [otpSent, setOtpSent] = useState(null);
  const [code, setCode] = useState('');
  // passphrase
  const [pass, setPass] = useState('');
  const [pass2, setPass2] = useState('');
  const [hint, setHint] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [keyReady, setKeyReady] = useState(hasUnlockedKey());
  // referti
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [viewer, setViewer] = useState(null); // { url, meta }
  const [decrypting, setDecrypting] = useState(null);
  // Deep-link dal Magic Link della notifica: /portale?report=<id> → evidenzia il referto.
  const [params] = useSearchParams();
  const highlightId = params.get('report');

  const refreshSession = useCallback(async () => {
    try { const s = await portalSession(); setSession(s); return s; } catch { setSession({ error: true }); return null; } finally { setLoading(false); }
  }, []);

  useEffect(() => { refreshSession(); }, [refreshSession]);

  const loadReports = useCallback(async () => {
    setReportsLoading(true);
    try { setReports(await listMyReports()); } catch (e) {
      if (e?.response?.status === 428) { portalLock(); setKeyReady(false); refreshSession(); toast.warning('Sessione portale scaduta: ripeti la verifica OTP'); }
      else toast.error('Impossibile caricare i referti');
    } finally { setReportsLoading(false); }
  }, [refreshSession]);

  useEffect(() => { if (session?.unlocked && keyReady) loadReports(); }, [session?.unlocked, keyReady, loadReports]);

  useEffect(() => {
    if (!highlightId || reports.length === 0) return;
    const el = document.querySelector(`[data-testid="portal-report-${highlightId}"]`);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [highlightId, reports]);

  // ---------------------------------------------------------------- OTP
  const requestOtp = async () => {
    setBusy(true);
    try {
      const r = await portalOtpRequest();
      setOtpSent(r);
      if (r.mock && r.debug_code) { setCode(r.debug_code); toast.info(`Modalità demo: codice ${r.debug_code}`); }
      else toast.success(`Codice inviato a ${r.sent_to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Invio OTP non riuscito'); } finally { setBusy(false); }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault?.();
    if (code.length !== 6) return;
    setBusy(true);
    try { await portalOtpVerify(code); toast.success('Identità verificata'); setCode(''); await refreshSession(); } catch (err) { toast.error(err?.response?.data?.detail || 'Codice non valido'); } finally { setBusy(false); }
  };

  // ---------------------------------------------------------------- chiavi
  const createKeys = async (e) => {
    e?.preventDefault?.();
    if (pass.length < 8) { toast.error('Passphrase troppo corta (min 8 caratteri)'); return; }
    if (pass !== pass2) { toast.error('Le passphrase non coincidono'); return; }
    setBusy(true);
    try {
      const r = await createPatientKeys(pass, hint.trim() || null);
      toast.success('Chiavi generate nel tuo browser. Conserva la passphrase: non è recuperabile.');
      setPass(''); setPass2(''); setKeyReady(true);
      setSession((s) => ({ ...s, has_keys: true, key_fingerprint: r.fingerprint }));
    } catch (err) { toast.error(err?.response?.data?.detail || err.message || 'Generazione chiavi non riuscita'); } finally { setBusy(false); }
  };

  const unlockKeys = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try { await unlockPatientKeys(pass); setPass(''); setKeyReady(true); toast.success('Chiave privata sbloccata (solo in memoria)'); } catch (err) {
      if (err?.response?.status === 428) { portalLock(); refreshSession(); }
      toast.error(err?.response?.data?.detail || 'Passphrase errata');
    } finally { setBusy(false); }
  };

  const lockAll = () => { portalLock(); setKeyReady(false); setViewer(null); setReports([]); refreshSession(); toast.info('Portale bloccato'); };

  // ---------------------------------------------------------------- referti
  const openReport = async (r, mode) => {
    setDecrypting(r.id);
    try {
      const { bytes, meta } = await decryptReport(r.id);
      if (mode === 'download') downloadBuffer(bytes, meta.filename || 'referto.pdf', meta.mime || 'application/pdf');
      else {
        const url = URL.createObjectURL(new Blob([bytes], { type: meta.mime || 'application/pdf' }));
        setViewer({ url, meta });
      }
      setReports((list) => list.map((x) => (x.id === r.id ? { ...x, opened_at: x.opened_at || new Date().toISOString(), status: 'opened' } : x)));
    } catch (err) { toast.error(err?.response?.data?.detail || err.message || 'Decifratura non riuscita'); } finally { setDecrypting(null); }
  };

  const closeViewer = () => { if (viewer?.url) URL.revokeObjectURL(viewer.url); setViewer(null); };

  // ---------------------------------------------------------------- render
  if (loading) return <div className="space-y-4" data-testid="portal-loading"><Skeleton className="h-24 rounded-xl" /><Skeleton className="h-64 rounded-xl" /></div>;

  if (session && !session.is_patient) {
    return <Card className="rounded-xl"><CardContent className="p-6 text-sm text-muted-foreground" data-testid="portal-not-patient">Il Portale cifrato è riservato ai pazienti. I professionisti inviano i referti da <b>Referti cifrati</b>.</CardContent></Card>;
  }

  const unlocked = !!session?.unlocked && !!getPortalToken();

  return (
    <div className="space-y-5" data-testid="patient-portal">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center"><LockKeyhole className="h-5 w-5" /></div>
          <div>
            <h1 className="font-display font-bold text-xl">Portale Paziente</h1>
            <p className="text-xs text-muted-foreground">I tuoi referti, leggibili solo da te: la decifratura avviene in questo browser.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <E2EBadge />
          {unlocked && <Button variant="outline" size="sm" onClick={lockAll} className="rounded-xl h-9" data-testid="portal-lock-btn"><Lock className="h-4 w-4" /> Blocca</Button>}
        </div>
      </div>

      {highlightId && !unlocked && (
        <div className="rounded-xl border border-primary/40 bg-primary/5 p-3 text-sm flex items-center gap-2" data-testid="portal-new-report-banner">
          <FileText className="h-4 w-4 text-primary flex-shrink-0" /> Un nuovo referto ti aspetta: completa la verifica OTP e sblocca la passphrase per leggerlo.
        </div>
      )}

      {/* Stepper */}
      <ol className="grid grid-cols-3 gap-2" data-testid="portal-stepper">
        {[['otp', 'Verifica OTP', Mail, unlocked], ['key', 'Passphrase', KeyRound, unlocked && keyReady], ['docs', 'Referti', FileText, unlocked && keyReady]].map(([id, label, Icon, done], i) => {
          const active = !done && (i === 0 ? true : (i === 1 ? unlocked : unlocked && keyReady));
          return (
            <li key={id} className={cn('rounded-xl border p-2.5 flex items-center gap-2', done ? 'border-success/40 bg-success/5' : active ? 'border-primary bg-primary/5' : 'border-border/70 bg-card')} data-testid={`portal-step-${id}`}>
              <span className={cn('h-7 w-7 rounded-lg flex items-center justify-center text-xs font-bold', done ? 'bg-success text-success-foreground' : active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')}>{done ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}</span>
              <span className="text-xs font-semibold flex items-center gap-1"><Icon className="h-3.5 w-3.5" /> {label}</span>
            </li>
          );
        })}
      </ol>

      {/* STEP 1 — OTP */}
      {!unlocked && (
        <Card className="rounded-xl" data-testid="portal-otp-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Mail className="h-4 w-4 text-primary" /> Verifica in due passaggi</CardTitle>
            <CardDescription>Ti inviamo un codice a 6 cifre all'email <b>{user?.email}</b>. Vale 10 minuti.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!otpSent ? (
              <Button onClick={requestOtp} disabled={busy} className="h-11 rounded-xl" data-testid="portal-otp-request-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Invia codice</Button>
            ) : (
              <form onSubmit={verifyOtp} className="space-y-4">
                {otpSent.mock && <div className="rounded-xl border border-warning/50 bg-warning/10 p-3 text-xs flex gap-2" data-testid="portal-otp-mock-banner"><AlertTriangle className="h-4 w-4 text-warning flex-shrink-0" /> Email non configurata (modalità demo): il codice è stato precompilato. In produzione arriva via email.</div>}
                <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                  <div>
                    <Label className="text-xs">Codice ricevuto</Label>
                    <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="portal-otp-input">
                      <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-11 w-10 text-base" />)}</InputOTPGroup>
                    </InputOTP>
                  </div>
                  <Button type="submit" disabled={busy || code.length !== 6} className="h-11 rounded-xl" data-testid="portal-otp-verify-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlock className="h-4 w-4" />} Verifica</Button>
                  <Button type="button" variant="ghost" onClick={requestOtp} disabled={busy} className="h-11 rounded-xl" data-testid="portal-otp-resend-btn"><RefreshCw className="h-4 w-4" /> Reinvia</Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      )}

      {/* STEP 2 — Passphrase */}
      {unlocked && !keyReady && (
        <Card className="rounded-xl" data-testid="portal-key-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><KeyRound className="h-4 w-4 text-primary" /> {session?.has_keys ? 'Sblocca la tua chiave privata' : 'Attiva la cifratura personale'}</CardTitle>
            <CardDescription>
              {session?.has_keys
                ? 'La passphrase decifra la tua chiave privata solo in questo browser. Non viene mai inviata al server.'
                : 'Generiamo nel tuo browser una coppia di chiavi RSA-2048. La chiave privata viene protetta con una passphrase che conosci solo tu: senza di essa nessuno (nemmeno la clinica) può leggere i referti.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={session?.has_keys ? unlockKeys : createKeys} className="space-y-3 max-w-md">
              <div>
                <Label>Passphrase</Label>
                <div className="flex gap-2">
                  <Input type={showPass ? 'text' : 'password'} value={pass} onChange={(e) => setPass(e.target.value)} className="h-11 rounded-xl" autoComplete="off" data-testid="portal-passphrase" />
                  <Button type="button" variant="outline" onClick={() => setShowPass((v) => !v)} className="h-11 w-11 rounded-xl p-0" aria-label="Mostra/nascondi" data-testid="portal-passphrase-toggle">{showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
                </div>
                {session?.has_keys && session?.key_hint && <p className="text-[11px] text-muted-foreground mt-1">Suggerimento: <i>{session.key_hint}</i></p>}
              </div>
              {!session?.has_keys && (
                <>
                  <div><Label>Conferma passphrase</Label><Input type={showPass ? 'text' : 'password'} value={pass2} onChange={(e) => setPass2(e.target.value)} className="h-11 rounded-xl" autoComplete="off" data-testid="portal-passphrase-confirm" /></div>
                  <div><Label>Suggerimento (facoltativo, mai la passphrase)</Label><Input value={hint} onChange={(e) => setHint(e.target.value)} maxLength={80} className="h-11 rounded-xl" data-testid="portal-passphrase-hint" /></div>
                  <div className="rounded-xl border border-warning/50 bg-warning/10 p-3 text-xs flex gap-2"><AlertTriangle className="h-4 w-4 text-warning flex-shrink-0" /> Se dimentichi la passphrase i referti già cifrati non saranno più recuperabili: è il prezzo della privacy assoluta.</div>
                </>
              )}
              <Button type="submit" disabled={busy || !pass} className="h-11 rounded-xl" data-testid="portal-key-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Fingerprint className="h-4 w-4" />} {session?.has_keys ? 'Sblocca' : 'Genera chiavi nel browser'}</Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* STEP 3 — Referti */}
      {unlocked && keyReady && (
        <Card className="rounded-xl" data-testid="portal-reports-card">
          <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /> I tuoi documenti cifrati</CardTitle>
              <CardDescription className="flex items-center gap-1 mt-1"><Fingerprint className="h-3 w-3" /> Impronta chiave: <span className="font-mono" data-testid="portal-key-fingerprint">{(session?.key_fingerprint || '').slice(0, 16)}…</span></CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={loadReports} disabled={reportsLoading} className="rounded-xl" data-testid="portal-reports-refresh"><RefreshCw className={cn('h-4 w-4', reportsLoading && 'animate-spin')} /></Button>
          </CardHeader>
          <CardContent>
            <ReportsList reports={reports} loading={reportsLoading} highlightId={highlightId} decrypting={decrypting} onOpen={openReport} />
          </CardContent>
        </Card>
      )}

      <ReportViewerDialog viewer={viewer} onClose={closeViewer} />
    </div>
  );
}
